import React, { useEffect, useRef, useState } from 'react';
import { normalize } from '../../main-page/home-utils';
import { ICON_PATHS } from '../../../components/shared-components/icon-paths';

// lists longer than this get a search field
const SEARCH_FROM = 8;

const isTypedChar = (e) => e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey;

// the next of `labels` after `current` that starts with the typed character, so pressing
// "s" again goes on from "Salonac" to "Stan"; -1 when none does
const jumpIndex = (labels, current, key) => {
    const char = normalize(key);
    for (let i = 1; i <= labels.length; i++) {
        const index = (current + i + labels.length) % labels.length;
        if (normalize(String(labels[index])).startsWith(char)) return index;
    }
    return -1;
};

// one step up or down a list of `count`, wrapping around; from nothing Down starts at the top and Up at the bottom
const stepIndex = (current, count, step) => (Math.max(current, step > 0 ? -1 : 0) + step + count) % count;

export const Check = ({ size = 13, width = 3 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
);

// a numbered card of the form; status is 'done', 'bad' or empty
export const Section = ({ id, number, status, title, sub, action, children }) => (
    <section id={id} className='hs-nl-card'>
        <div className='hs-nl-card-head'>
            <span className={`hs-nl-step ${status}`}>{status === 'done' ? <Check size={15} /> : number}</span>
            <div className='hs-nl-card-title'>
                <h2>{title}</h2>
                {sub && <p>{sub}</p>}
            </div>
            {action}
        </div>
        {children}
    </section>
);

export const Field = ({ label, required, suffix, error, hint, full, ...input }) => (
    <div className={`hs-nl-field-wrap ${full ? 'full' : ''}`}>
        <label className={`hs-nl-field ${error ? 'bad' : ''}`}>
            <span className='hs-nl-field-main'>
                <span className='hs-nl-field-label'>{label}{required && ' *'}</span>
                <input {...input} />
            </span>
            {suffix && <span className='hs-nl-field-suffix'>{suffix}</span>}
        </label>
        {error ? <span className='hs-nl-error'>{error}</span> : hint && <span className='hs-nl-hint'>{hint}</span>}
    </div>
);

// options are { id, label }; value is the id of the picked one
// keyboard: a letter or digit (or the arrow keys) opens the list and marks the next option starting
// with it, or types into the search field of a long list; Enter picks the marked option
export const Dropdown = ({ label, required, value, options, onPick, disabled, placeholder, searchLabel, error }) => {
    const ref = useRef(null);
    const buttonRef = useRef(null);
    const panelRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    // the option marked with the keyboard or the mouse, as an index into the shown options
    const [active, setActive] = useState(-1);
    const searchable = options.length > SEARCH_FROM;

    const close = () => {
        setOpen(false);
        setQuery('');
        buttonRef.current?.focus();
    };

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            setOpen(false);
            setQuery('');
            buttonRef.current?.focus();
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const selected = options.find(o => o.id === value);
    const shown = query ? options.filter(o => normalize(String(o.label)).includes(normalize(query))) : options;
    const pick = (option) => {
        onPick(option);
        close();
    };
    const mark = (index) => {
        setActive(index);
        // only the keyboard scrolls the list: scrolling on hover would move another row under the mouse
        panelRef.current?.querySelectorAll('button')[index]?.scrollIntoView({ block: 'nearest' });
    };
    const openAt = (index) => {
        setOpen(true);
        setActive(index);
        // the panel is not there yet, so scroll once it has rendered
        setTimeout(() => panelRef.current?.querySelectorAll('button')[index]?.scrollIntoView({ block: 'nearest' }), 0);
    };
    const toggle = () => open ? close() : openAt(options.indexOf(selected));

    const onKeyDown = (e) => {
        if (disabled || e.key === 'Tab') {
            // Tab goes on to the next field, so the list closes without taking the focus back
            setOpen(false);
            setQuery('');
            return;
        }
        const arrow = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
        if (!open) {
            if (arrow) {
                e.preventDefault();
                openAt(options.indexOf(selected));
            } else if (isTypedChar(e)) {
                e.preventDefault();
                if (searchable) {
                    setQuery(e.key);
                    openAt(0);
                } else {
                    openAt(jumpIndex(options.map(o => o.label), options.indexOf(selected), e.key));
                }
            }
            return;
        }
        if (shown.length === 0) return;
        if (arrow) {
            e.preventDefault();
            mark(stepIndex(active, shown.length, arrow));
        } else if (e.key === 'Enter' && active >= 0 && shown[active]) {
            e.preventDefault();
            pick(shown[active]);
        } else if (!searchable && isTypedChar(e)) {
            const next = jumpIndex(shown.map(o => o.label), active, e.key);
            if (next >= 0) {
                e.preventDefault();
                mark(next);
            }
        }
    };

    return (
        <div className='hs-nl-field-wrap hs-nl-dd' ref={ref} onKeyDown={onKeyDown}>
            <button ref={buttonRef} type='button' disabled={disabled} aria-expanded={open}
                className={`hs-nl-dd-button ${open ? 'open' : ''} ${error ? 'bad' : ''}`} onClick={toggle}>
                <span className='hs-nl-field-label'>{label}{required && ' *'}</span>
                <span className={`hs-nl-dd-value ${selected ? '' : 'empty'}`}>
                    <span>{selected ? selected.label : placeholder}</span>
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1.5 6 6.5 11 1.5" stroke="#5B6779" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </span>
            </button>
            {open &&
                <div className='hs-nl-dd-panel' ref={panelRef}>
                    {searchable &&
                        <input className='hs-nl-dd-search' autoFocus value={query} placeholder={searchLabel}
                            onChange={(e) => { setQuery(e.target.value); setActive(e.target.value ? 0 : -1); }} />}
                    {shown.map((option, i) => (
                        <button type='button' key={option.id} tabIndex={-1}
                            className={`${option.id === value ? 'on' : ''} ${i === active ? 'active' : ''}`}
                            onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setActive(i)} onClick={() => pick(option)}>
                            {option.label}{option.id === value && <Check size={16} width={2.6} />}
                        </button>
                    ))}
                </div>}
            {error && <span className='hs-nl-error'>{error}</span>}
        </div>
    );
};

// once a chip has the focus, the arrow keys move between the chips and a letter or digit jumps to the
// next chip starting with it ("s" → "Stan", "2" → "2.0"); Enter or Space picks it
const onChipsKeyDown = (e) => {
    const chips = Array.from(e.currentTarget.querySelectorAll('button'));
    const current = chips.indexOf(document.activeElement);
    if (current < 0) return;
    const arrow = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    const next = arrow ? stepIndex(current, chips.length, arrow)
        : isTypedChar(e) ? jumpIndex(chips.map(chip => chip.textContent.trim()), current, e.key) : -1;
    if (next < 0) return;
    e.preventDefault();
    chips[next].focus();
};

// options are { key, label, on }
export const ChipGroup = ({ label, note, required, error, options, onToggle, check }) => (
    <div className='hs-nl-group'>
        <span className='hs-nl-group-label'>{label}{required && ' *'}{note && <em>{note}</em>}</span>
        <div className='hs-nl-chips' onKeyDown={onChipsKeyDown}>
            {options.map(option => (
                <button type='button' key={option.key} className={option.on ? 'on' : ''} onClick={() => onToggle(option.key)}>
                    {check && option.on && <Check />}{option.label}
                </button>
            ))}
        </div>
        {error && <span className='hs-nl-error'>{error}</span>}
    </div>
);

export const Confirm = ({ title, confirmLabel, cancelLabel, busy, onConfirm, onClose, children }) => {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [busy, onClose]);

    return (
        <div className='hs hs-nl-overlay' onClick={() => !busy && onClose()}>
            <div className='hs-nl-dialog' role='dialog' aria-label={title} onClick={(e) => e.stopPropagation()}>
                <span className='hs-nl-dialog-icon'>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={ICON_PATHS.warning} /></svg>
                </span>
                <h2>{title}</h2>
                <p>{children}</p>
                <div className='hs-nl-dialog-actions'>
                    <button className='hs-nl-outline' disabled={busy} onClick={onClose}>{cancelLabel}</button>
                    <button className='hs-nl-publish' disabled={busy} onClick={onConfirm}>{confirmLabel}</button>
                </div>
            </div>
        </div>
    );
};
